import { SyntheticEvent, useCallback, useMemo } from "react";
import { IPanelProps, Panel, PanelType } from "@fluentui/react/lib/Panel";
import { IRenderFunction } from "@fluentui/react/lib/Utilities";

import { isNotNullOrWhiteSpace } from "Utils/String";
import { generateUniqueId } from "Utils/UniqueId";
import { isIdInParents } from "Utils/DOM";
import { useCustomPanelStyles } from "./UseCustomPanelStyles";
import { ICustomPanelProps } from "./ICustomPanelProps";
import { WithTranslator } from "../Translator";
import { Header } from "./Header";
import { CustomPanelSizeType } from "./CustomPanelSizeType";

export const CustomPanel = WithTranslator((props: ICustomPanelProps): JSX.Element => {
  const {
    id,
    children,
    onDismiss,
    headerTitle,
    translator,
    customPanelSize = CustomPanelSizeType.small,
    ...rest
  } = props;
  const styles = useCustomPanelStyles();

  const internalID = useMemo(() => (isNotNullOrWhiteSpace(id) ? id : generateUniqueId(true)), [id]);

  const onDismisInt = useCallback(
    (ev?: SyntheticEvent<HTMLElement> | KeyboardEvent) => {
      if (!isIdInParents(ev?.currentTarget, internalID)) {
        // ID cannot be found in parent hierarchy so this event
        // is not for us!
        return;
      }

      onDismiss(ev);
    },
    [internalID, onDismiss]
  );

  const onRenderNavigationContent: IRenderFunction<IPanelProps> = useCallback(
    (p, defaultRender) => (
      <>
        <Header {...props} />
        {defaultRender(p)}
      </>
    ),
    [props]
  );

  return (
    <Panel
      type={PanelType.custom}
      isHiddenOnDismiss={false}
      popupProps={{ id: internalID }}
      onDismiss={onDismisInt}
      closeButtonAriaLabel={translator.get(`generic.close`, `Close`)}
      className={styles.root}
      overlayProps={{ isDarkThemed: true }}
      onRenderNavigationContent={onRenderNavigationContent}
      customWidth={customPanelSize}
      {...rest}
    >
      {children}
    </Panel>
  );
});
